import { ColoredChipIconButton } from '@components/colored-elements';
import { createCFFTextAttr } from '@components/context-form-field';
import { ScrollSectionTitle } from '@components/scroll-section-title';
import { TrashFill } from '@icon/react-icons';
import { Divider, Grid, GridSize, Tooltip, Typography, TextFieldProps, InputAdornment, Select, TextField, FormControlLabel, IconButton} from '@material-ui/core';
import { indigo, red } from '@material-ui/core/colors';
import AddIcon from '@material-ui/icons/Add';
import BallotRoundedIcon from '@material-ui/icons/BallotRounded';
import HttpIcon from '@material-ui/icons/Http';
import ClearIcon from '@material-ui/icons/Clear';
import { SubSectionTitle } from 'pages/company/form/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { createTextAttr } from '../fields';
import styled from 'styled-components';
import Fab from '@material-ui/core/Fab';
import { Checkbox } from '@material-ui/core';
import { ContextFormField } from '@components/context-form-field';
import { DatePicker } from '@material-ui/pickers';
import { createControlField } from '@lib/common';

export type CompanyBasicFormFieldProps = TextFieldProps & {
    errorKeys?: Array<string>;
    selectOptions?: { label: string; value: string | number }[];
    hideEmptySelect?: boolean;
    icon?: React.ReactNode;
    dropdown?: Boolean;
    options?: {value: string, name: string}[];
    hidden?: Boolean;
  };
  
  export const CompanyBasicFormField = ({
    errorKeys = [],
    selectOptions,
    hideEmptySelect = false,
    icon,
    type="string",
    dropdown = false,
    options = [],
    hidden = false,
    ...props
  }: CompanyBasicFormFieldProps) => {
    const selectProps = useMemo(
      () =>
        props.select
          ? {
              select: true,
              SelectProps: { ...props.SelectProps, native: true },
              children: (
                <>
                  {!hideEmptySelect && <option value=""></option>}
                  {selectOptions.map(({ label, value = label }) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </>
              ),
            }
          : null,
      [selectOptions, props.select, props.SelectProps, hideEmptySelect]
    );
  
    const iconProps = useMemo(
      () =>
        icon
          ? {
              InputProps: {
                ...props.InputProps,
                startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
              },
            }
          : null,
      [icon, props.InputProps]
    );
  
    const { register, errors, setValue} = useFormContext();
  
    const onChange = (event)=>{
      setValue(props?.name, event.target.value);
    }
  
    const errorProps = useMemo(() => {
      const messages = [props.name]
        .concat(errorKeys)
        .filter(k => k && errors && errors[k])
        .map(k => `${errors[k]?.message}`);
      if (errors && messages.length) {
        return {
          error: true,
          helperText: messages.join(', '),
        };
      }
      return null;
    }, [errors, errorKeys]);
    return (
      <>
        {dropdown ?
        <Select
          variant="outlined"
          style={{height: "16px",padding: "20px", overflow:"hidden"}}
          fullWidth
          {...selectProps}
          {...iconProps}
          {...errorProps}
          id={props.name}
          name={props.name}
          native
          defaultValue=""
          inputRef={register}
          onChange={onChange}
        >
          {
            options?.map(({value, name})=>{
              return <option value={value}>{name}</option>
            })
          }
        </Select>
        :
        <TextField
        variant="outlined"
        size="small"
        fullWidth
        type={type}
        style={{display:`${hidden ? "none":""}`}}
        {...props}
        {...selectProps}
        {...iconProps}
        {...errorProps}
        onChange={onChange}
        inputRef={register}
      />}
      </>
    );
  };
  export const ContentDateFormField = ({
    errorKeys = [],
    selectOptions,
    hideEmptySelect = false,
    icon,
    dropdown = false,
    options = [],
    ...props
  }: CompanyBasicFormFieldProps) => {
  
    const { control } = useFormContext();
    return (
      <>
        <DateField label={`${props?.label}`} helperText={`${props?.helperText}`} control={control} name={props?.name}/>
      </>
    );
  };

  function isValidDate(dateString) {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  export const DateField = createControlField(props => {
    const { onChange, value } = props;
  
    const handleChange = (newValue) => {
      if (newValue && isValidDate(newValue)) {
        // Set the day to the 1st of the month
        const updatedDate = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
        onChange(updatedDate);
      } else {
        onChange(null);
      }
    };
  
    const handleClear = (event) => {
      event.stopPropagation();
      onChange(null);
    };
  
    return (<DatePicker
      fullWidth
      style={{ marginTop: 0, marginBottom: 0 }}
      inputVariant="outlined"
      views={["month", "year"]}
      margin="normal"
      value={value || null}
      onChange={handleChange}
      size="small"
      {...props}
      InputProps={{
        endAdornment: (
          <>
            {value && (
              <InputAdornment position="end" style={{ marginRight: '-12px' }}>
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon fontSize="small"/>
                </IconButton>
              </InputAdornment>
            )}
          </>
        )
      }}
    />
  )});

const StyledFab = styled(Fab)`
  &.MuiFab-sizeSmall {
    width: 40px;
    // height: 30px;
  }
  & .MuiSvgIcon-root {
    font-size: 1.4rem;
  }
`;

export const TVSeriesSeasonInfo = ({initialSeasonLen=0}) => {
  const form = useFormContext();

  const imdbAttrs = [
    createTextAttr('season_names','Season Name',"Please enter Season Name."),
    createTextAttr('season_numbers','Season',"Please enter Season number"),
    createTextAttr('series_filming_start_dates', 'Filming Start Month/Year.', 'Enter Season filming start month/year.'),
    createTextAttr('series_filming_end_dates', 'Filming End Month/Year.', 'Enter Season filming end month/year.'),
    createTextAttr('season_aired_on_dates', 'First Aired On date', 'Enter Aired On Date.'),
    createTextAttr('season_vtr_id','',''),
  ];

  const initialNumberofUrls = initialSeasonLen;
  const [indexesAttributes, setIndexesAttributes] = useState(
    Array.from(Array(initialNumberofUrls || 1), (_, i) => i)
  );

  const [counterAttributes, setCounterAttributes] = useState(initialNumberofUrls || 1);

  const companySelected : any = form.watch("buyers");

  const addAttributes = useCallback(() => {
    setIndexesAttributes(prevIndexes => {
      return [...prevIndexes, counterAttributes];
    });
    setCounterAttributes(prevCounter => prevCounter + 1);
  }, [indexesAttributes, counterAttributes]);

  const removeAttributes = useCallback(index => {
    setIndexesAttributes(prevIndexes => {
      return [...prevIndexes.filter(item => item !== index)];
    });
  }, []);

  return (
    <>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '1rem' }}>
        <Grid item>
          <ScrollSectionTitle
            id="season-info"
            icon={
              <BallotRoundedIcon style={{ color: indigo[700] }} fontSize="large" />
            }
            subTitle="Additional Information of season ."
          >
              Seasons Info
          </ScrollSectionTitle>
        </Grid>
        <Grid item style={{ marginLeft: 'auto' }}>
          <Tooltip title="Add IMDb details">
            <StyledFab
              disabled={false}
              onClick={addAttributes}
              color="primary"
              aria-label="add"
              size="small">
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </Grid>
      </Grid>
      <Divider />
      <br />
      <fieldset disabled={false} style={{ "border": "none" }}>
        <Grid
          container
          spacing={1}
          justify="space-between">
          {indexesAttributes.map(indexOfAttribute => {
            return (
              <Grid
                container
                style={{ display: 'flex',gap: "20px"}}
                key={indexOfAttribute}>
                {imdbAttrs.map((attr, i) => (
                  <>
                    <Grid
                      item
                      key={i}
                      xs={(2) as unknown as GridSize} 
                      style={{flexBasis: 'fit-content'}}>
                      <Typography variant="h6" gutterBottom>
                        {attr?.label}{attr?.label === "Domain" && <span style={{ color: 'red' }}>*</span>}
                      </Typography>
                      {attr.name === 'season_names' ? (
                        <CompanyBasicFormField
                          {...attr}
                          name={`season_names[${indexOfAttribute}]`}
                        />
                      ) : attr.name === 'season_numbers' ? (
                        <CompanyBasicFormField
                          {...attr}
                          type="number"
                          name={`season_numbers[${indexOfAttribute}]`}
                        />
                      ) : attr.name === 'series_filming_start_dates' ? (
                        <ContentDateFormField
                          {...attr}
                          name={`series_filming_start_dates[${indexOfAttribute}]`}
                        />
                      ) : attr.name === 'series_filming_end_dates' ? (
                        <ContentDateFormField
                          {...attr}
                          name={`series_filming_end_dates[${indexOfAttribute}]`}
                        />
                      ) : attr.name === 'season_aired_on_dates' ? (
                        <ContentDateFormField
                          {...attr}
                          name={`season_aired_on_dates[${indexOfAttribute}]`}
                        />
                      ) : (
                        // Hidden field: season_vtr_id
                        <div style={{ display: 'none' }}>
                          <CompanyBasicFormField {...attr} name={`season_vtr_id[${indexOfAttribute}]`} />
                        </div>
                      )}
                    </Grid>
                  </>
                ))}
                <Grid
                  item
                  xs={1}
                  style={{ display: 'flex', alignItems: 'end', marginBottom: '30px', scale: "1.4", justifyContent:"center"}}>
                  <div
                    style={{
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {indexesAttributes?.length > 1 && <Tooltip title="Delete Atrribute">
                      <ColoredChipIconButton
                        onClick={() => removeAttributes(indexOfAttribute)}
                        pointer
                        colored={red[900]}
                        size="small"
                        icon={<TrashFill />}
                      />
                    </Tooltip>}
                  </div>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </fieldset>
    </>
  );
};
